import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../../utils/toast';
import { StateSwitch } from '../../../motion';
import { spacing } from '../../../style-utils/theme';
import { DefaultComponentProps } from '../../app-router/component-props';
import { BackBar, BackButton } from '../../layout/back-button';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import { useIqEntitlement } from '../../hooks/use-iq-entitlement';
import { generateHandover } from '../../../api/iq-client';
import { ShiftSummaryResponse } from '../../../interfaces/iq-interfaces';
import { IqSummaryCard } from '../../iq/iq-summary-card';
import { IqRawLog } from '../../iq/iq-raw-log';
import { IqAskPanel } from '../../iq/iq-ask-panel';
import { IqOnDutyBlock } from '../../iq/iq-on-duty-block';
import {
  IqPage,
  IqInner,
  IqHeaderRow,
  IqTitle,
  IqSub,
  Caption,
} from '../../iq/iq-shared.styled';
import { buildingWindowLabel } from '../../../function-utils/building-time';

/* NO PAGE-LEVEL ENTRANCE: the route layer (RouteTransition, keyed by shell
   group) moves this page. What moves HERE is the body — checking → not
   enabled → the report — crossfaded in place, keeping IqInner's column gap
   inside the switch. */
const PageBody = styled(StateSwitch)`
  > div {
    display: flex;
    flex-direction: column;
    gap: ${spacing.md};
  }
`;

/* A handover is a BUILDING record — see buildingWindowLabel. */
const fmtWindow = (from: string, to: string, tz: string): string =>
  buildingWindowLabel(from, to, tz);

// `navigate` is no longer destructured off the router props: the only thing that
// used it was the page's own back button, and that is now <BackButton>, which
// owns its navigation. Leaving the binding in place would be an unused prop that
// reads as a page still doing its own routing.
export const IqHandoverPage: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
}) => {
  const { permissions } = useAuth();
  const enabledProbe = can(permissions, PERMISSIONS.IQ_USE);
  const { state: entitlement, loading: entLoading } = useIqEntitlement({
    enabled: enabledProbe,
  });

  const [data, setData] = useState<ShiftSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [flashId, setFlashId] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const buildingId = selectedBuilding ? Number(selectedBuilding.id) : NaN;
  const buildingName = selectedBuilding?.property_name || 'this building';

  const run = async (force: boolean): Promise<void> => {
    if (!Number.isInteger(buildingId)) return;
    controllerRef.current?.abort();
    const ac = new AbortController();
    controllerRef.current = ac;
    setLoading(true);
    try {
      const res = await generateHandover(
        { building_id: buildingId, force },
        { signal: ac.signal }
      );
      setData(res);
    } catch (e) {
      if (ac.signal.aborted) return;
      const message =
        e instanceof Error ? e.message : 'Could not reach Lobbero IQ.';
      toast.error(message);
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  };

  const scrollToSource = (id: string): void => {
    const el = document.getElementById(`iq-entry-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(id);
    window.setTimeout(() => setFlashId(null), 1200);
  };

  const notEnabled = !entLoading && (!entitlement || !entitlement.enabled);
  const windowLabel = data
    ? fmtWindow(data.window.from, data.window.to, data.window.tz)
    : 'The last completed shift for this building.';

  const detail = data?.degraded?.detail;
  const detailTotal =
    detail && typeof detail === 'object' ? detail.total : undefined;
  const totalEntries =
    detailTotal ?? data?.total ?? (data ? data.entries.length : 0);

  return (
    <IqPage>
      <IqInner>
        {/* TOP LEFT, IN ITS OWN ROW (WP-10 / R-9). This button used to sit
            INSIDE IqHeaderRow, which is justify-content: space-between — so it
            rendered top RIGHT, and the comment that used to be here said so
            approvingly. That contradicts the rule set on 2026-08-11 (back is
            top left everywhere) and it made this the one screen where the way
            out was somewhere else. Lifted out, above the row, exactly as
            account-page.tsx:141-146 does it. */}
        <BackBar>
          <BackButton to="/main-menu" label="Back to menu" />
        </BackBar>
        <IqHeaderRow>
          <div>
            {/* "SHIFT REPORT", NOT "HANDOVER". This page generates a report
                about a window (POST /iq/shift-summary) — it writes nothing,
                closes no shift, and can be re-run. The word HANDOVER belongs to
                the frozen record written when a shift ends, which is a different
                route on a different screen and warns before it acts. Sharing the
                word made the harmless control look like the consequential one:
                owner report, 2026-08-21, "handover is bugged from the iq, there
                should be a warning that the shift will close." */}
            <IqTitle>Shift report</IqTitle>
            <IqSub>{buildingName}</IqSub>
          </div>
        </IqHeaderRow>

        <PageBody
          state={entLoading ? 'checking' : notEnabled ? 'off' : 'ready'}
          minHeight={48}
        >
          {entLoading ? (
            <Caption>Checking Lobbero IQ…</Caption>
          ) : notEnabled ? (
            <Caption>
              Lobbero IQ is not enabled for this account. Ask your account owner,
              or read the activity feed on the main menu.
            </Caption>
          ) : (
            <>
              <IqSummaryCard
                data={data}
                loading={loading}
                windowLabel={windowLabel}
                entryCount={data ? data.entries.length : null}
                canGenerate={Number.isInteger(buildingId)}
                /* WP-40 ride-along. Without this the prop defaults to TRUE, so
                   with no building chosen this page told the reader "Lobbero IQ
                   is not switched on for this account" — the wrong reason, and
                   one they cannot act on. */
                hasBuilding={Number.isInteger(buildingId)}
                onGenerate={() => run(false)}
                onForce={() => run(true)}
                onRetry={() => run(false)}
                onScrollToSource={scrollToSource}
              />
              {/* Same placement and the same reasoning as IqChat: it sits between
                  the AI card and the raw log. `on_duty` is absent, not null, for a
                  caller without user:read (iq.js:230-250), so this presence check
                  is the whole gate — nothing is fetched and hidden. */}
              {data?.on_duty ? (
                <IqOnDutyBlock data={data.on_duty} tz={data.window.tz} />
              ) : null}
              {data && data.entries.length ? (
                <IqRawLog
                  entries={data.entries}
                  tz={data.window.tz}
                  truncated={data.truncated}
                  entryLimit={data.window.entry_limit || data.entries.length}
                  total={totalEntries}
                  flashId={flashId}
                />
              ) : null}
              {/* Rendered unconditionally now: the guard moved INSIDE IqAskPanel
                  (it clears the composer before validating, so a mount without a
                  wrapper lost the typed question), and the panel states its own
                  reason rather than vanishing without one. */}
              <IqAskPanel buildingId={buildingId} />
            </>
          )}
        </PageBody>
      </IqInner>
    </IqPage>
  );
};
